import { Config } from '@backstage/config';
import { Environment } from '../types';

export const CONFIG_ENVIRONMENTS_PATH = 'mesh.builder.environments';

/**
 * Retrieve environments from configurations
 * You should not call this directly, you should rely on the EnvironmentService
 * @see EnvironmentService
 * @param config
 * @returns environments in lower case
 */
export function getEnvironmentsFromConfig(config: Config): Environment[] {
  const environments = config.getOptional(CONFIG_ENVIRONMENTS_PATH);
  // Check if the environments property is defined
  if (
    !environments ||
    !Array.isArray(environments) ||
    environments.length === 0
  ) {
    const noEnvironmentsError = `No environments defined in app-config. Please be sure to have a property "environments" with a list of environments in your app-config!`;
    throw new Error(noEnvironmentsError);
  }

  // Check if the environments array contains objects or strings
  const stringEnvs = environments
    .filter(env => typeof env === 'string')
    .map(env => ({ name: env!.toString().toLowerCase() }));
  // If all environments are strings, we can return them and assign a priority
  if (stringEnvs.length === environments.length) {
    return stringEnvs.map((env, i) => ({ name: env.name, priority: i }));
  }
  // If not, we need to check if the objects have the correct properties
  if (
    !environments.every(
      env =>
        typeof (env as any).name === 'string' &&
        typeof (env as any).priority === 'number',
    )
  ) {
    throw new Error(
      `Invalid environment configuration: ${JSON.stringify(
        environments,
      )}. You need to define for each env, a name and a priority`,
    );
  }
  // If everything is correct, we can return the environments
  return (environments as Record<string, any>[]).map(env => {
    return {
      name: env.name.toLowerCase(),
      priority: parseInt(env.priority ?? '', 10),
    };
  });
}

export const getModuleOverrideEnabledFromConfig = (config: Config): boolean => {
  return config.getOptionalBoolean('platform.enableOverrideModules') ?? false;
};
